import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Text,
  useTheme,
} from "@chakra-ui/react";

import { pickTextColorBasedOnBgColor } from "@picovico/utils";
import { get } from "lodash";
import { useRef } from "react";
import { SeatLayoutProps } from "../../@types/SeatLayout";
import { Screen } from "../../components/Screen";
import { seatColorEnum } from "../../constant";
import PanZoomPinchWrapper from "../PanZoomPinch/PanZoomPinchWrapper";

function SeatLayoutRenderer({
  seatLayouts,
  screenPosition,
  audiLayout,
  onSeatSelect,
  seatWrapperBoxProps,
  seatWrapperFlexProps,
  selectedSeats,
  disableSelectingSeats = false,
  screenSideBoxProps,
  screenTopScreenSideBoxPropsSx,
  screenBottomScreenSideBoxPropsSx,
  screenSideType = "BOX",
  seatButtonExtraProps,
  screenTextProps,
  panZoomPinchProps,
  showPanZoomPinchLayout = true,
}: SeatLayoutProps) {
  const theme = useTheme();
  const seatsRendererRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  if (!seatLayouts || seatLayouts?.length < 1) return null;
  return (
    <Flex
      justifyContent={
        screenPosition === "Bottom" ? "space-between" : "flex-end"
      }
      alignItems="center"
      direction={screenPosition === "Bottom" ? "column" : "column-reverse"}
      width="100vw"
      height="100%"
      py={{ base: "1rem", md: "0.5rem" }}
      px={{ base: "0.5rem", sm: "1rem" }}
      className={"seat-layout-box-wrapper"}
      gap={4}
      {...seatWrapperFlexProps}
    >
      {showPanZoomPinchLayout ? (
        <PanZoomPinchWrapper
          seatLayoutRef={seatsRendererRef}
          isDraggingRef={isDraggingRef}
          showZoomControls={false}
          initialScale={0.6}
          {...panZoomPinchProps}
        >
          <Flex
            ref={seatsRendererRef}
            width={"100%"}
            justifyContent={"center"}
            overflow={"auto"}
          >
            <Box
              {...seatWrapperBoxProps}
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: audiLayout === "row" ? "column" : "row",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                maxHeight: "100%",
                width: "fit-content",
                maxWidth: "100%",
                // overflow: "auto",
                ...(seatWrapperBoxProps?.sx || {}),
              }}
            >
              {seatLayouts?.map((seatLayout, index) => (
                <Grid
                  key={`seat_grid_${index}`}
                  templateRows={
                    audiLayout === "col"
                      ? `repeat(${seatLayout?.seats?.length},1fr)`
                      : ""
                  }
                  templateColumns={
                    audiLayout === "row"
                      ? `repeat(${seatLayout?.seats?.length},1fr)`
                      : ""
                  }
                  gap={"0.5rem"}
                  mb={"0.5rem"}
                  width={"100%"}
                  justifyItems={"center"}
                >
                  {seatLayout.seats?.map((seat, seatIndex) => {
                    const seatColor: string = selectedSeats?.includes(
                      seat.seatIdx,
                    )
                      ? seatColorEnum.SELECTED
                      : seat.seat_status === "sold"
                        ? seatColorEnum.SOLD
                        : seat.seat_status === "booked"
                          ? seatColorEnum.BOOKED
                          : seat.seat_status === "hold"
                            ? seatColorEnum.BOOKED
                            : seatColorEnum.AVAILABLE;

                    return (
                      <GridItem
                        maxW="2rem"
                        width={"100%"}
                        key={`seat_${seatIndex}`}
                      >
                        <Button
                          w="full"
                          overflow="hidden"
                          colorScheme="primary"
                          variant="showSeatsButton"
                          size="sm"
                          fontSize={"12px"}
                          opacity={1}
                          // minW={"fit-content"}
                          isDisabled={
                            !seat.canSelect ||
                            !seat.isActive ||
                            disableSelectingSeats
                          }
                          _disabled={{
                            cursor: "not-allowed",
                            opacity: 0.6,
                            _hover: {
                              background: seat.isActive
                                ? seatColor
                                : "transparent",
                              opacity: 0.7,
                            },
                          }}
                          _hover={{
                            background: seat.isActive
                              ? seatColor
                              : "transparent",
                            opacity: 0.7,
                          }}
                          border={seat.isCustomSeat ? "4px solid " : "none"}
                          // borderColor={seatColorEnum.PREMIUM}
                          onClick={() => {
                            if (isDraggingRef?.current) return;
                            onSeatSelect(seat);
                          }}
                          {...seatButtonExtraProps}
                          sx={{
                            p: "0.5rem",
                            background: seat.isActive
                              ? seatColor
                              : "transparent",

                            color: pickTextColorBasedOnBgColor(
                              get(theme.colors, seatColor) || seatColor,
                              theme?.colors?.white || "#fff",
                              theme?.colors?.surface[700] || "#111",
                            ),
                            ...(seatButtonExtraProps?.sx || {}),
                          }}
                        >
                          {seat?.isActive && seat.seatLabel}
                        </Button>
                      </GridItem>
                    );
                  })}
                </Grid>
              ))}
            </Box>
          </Flex>
        </PanZoomPinchWrapper>
      ) : (
        <Flex width={"100%"} justifyContent={"center"} overflow={"auto"}>
          <Box
            {...seatWrapperBoxProps}
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: audiLayout === "row" ? "column" : "row",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              maxHeight: "100%",
              width: "fit-content",
              maxWidth: "100%",
              // overflow: "auto",
              ...(seatWrapperBoxProps?.sx || {}),
            }}
          >
            {seatLayouts?.map((seatLayout, index) => (
              <Grid
                key={`seat_grid_${index}`}
                templateRows={
                  audiLayout === "col"
                    ? `repeat(${seatLayout?.seats?.length},1fr)`
                    : ""
                }
                templateColumns={
                  audiLayout === "row"
                    ? `repeat(${seatLayout?.seats?.length},1fr)`
                    : ""
                }
                gap={"0.5rem"}
                mb={"0.5rem"}
                width={"100%"}
                justifyItems={"center"}
              >
                {seatLayout.seats?.map((seat, seatIndex) => {
                  const seatColor: string = selectedSeats?.includes(
                    seat.seatIdx,
                  )
                    ? seatColorEnum.SELECTED
                    : seat.seat_status === "sold"
                      ? seatColorEnum.SOLD
                      : seat.seat_status === "booked"
                        ? seatColorEnum.BOOKED
                        : seat.seat_status === "hold"
                          ? seatColorEnum.BOOKED
                          : seatColorEnum.AVAILABLE;

                  return (
                    <GridItem
                      maxW="2rem"
                      width={"100%"}
                      key={`seat_${seatIndex}`}
                    >
                      <Button
                        w="full"
                        overflow="hidden"
                        colorScheme="primary"
                        variant="showSeatsButton"
                        size="sm"
                        fontSize={"12px"}
                        opacity={1}
                        // minW={"fit-content"}
                        isDisabled={
                          !seat.canSelect ||
                          !seat.isActive ||
                          disableSelectingSeats
                        }
                        _disabled={{
                          cursor: "not-allowed",
                          opacity: 0.6,
                          _hover: {
                            background: seat.isActive
                              ? seatColor
                              : "transparent",
                            opacity: 0.7,
                          },
                        }}
                        _hover={{
                          background: seat.isActive ? seatColor : "transparent",
                          opacity: 0.7,
                        }}
                        border={seat.isCustomSeat ? "4px solid " : "none"}
                        // borderColor={seatColorEnum.PREMIUM}
                        onClick={() => {
                          if (isDraggingRef?.current) return;
                          onSeatSelect(seat);
                        }}
                        {...seatButtonExtraProps}
                        sx={{
                          p: "0.5rem",
                          background: seat.isActive ? seatColor : "transparent",

                          color: pickTextColorBasedOnBgColor(
                            get(theme.colors, seatColor) || seatColor,
                            theme?.colors?.white || "#fff",
                            theme?.colors?.surface[700] || "#111",
                          ),
                          ...(seatButtonExtraProps?.sx || {}),
                        }}
                      >
                        {seat?.isActive && seat.seatLabel}
                      </Button>
                    </GridItem>
                  );
                })}
              </Grid>
            ))}
          </Box>
        </Flex>
      )}

      {screenSideType === "CURVE" ? (
        <Screen
          screenPosition={screenPosition}
          screenTextProps={screenTextProps}
          {...screenSideBoxProps}
        />
      ) : (
        <Box
          p="0.5rem"
          my={{
            base: "0.5rem",
            md: "1rem",
          }}
          width={{
            base: "80vw",
            md: "80vw",
            sm: "full",
          }}
          background="surface.500"
          {...screenSideBoxProps}
          sx={{
            position: "relative",
            textAlign: "center",
            color: "white",
            fontWeight: 600,
            borderBottomRightRadius:
              screenPosition === "Bottom" ? "3xl" : "none",
            borderBottomLeftRadius:
              screenPosition === "Bottom" ? "3xl" : "none",
            borderTopRightRadius: screenPosition === "Top" ? "none" : "3xl",
            borderTopLeftRadius: screenPosition === "Top" ? "none" : "3xl",
            ...screenSideBoxProps?.sx,
            ...(screenPosition === "Top"
              ? screenTopScreenSideBoxPropsSx
              : screenBottomScreenSideBoxPropsSx),
          }}
        >
          <Text>Screen this side</Text>
        </Box>
      )}
    </Flex>
  );
}

export default SeatLayoutRenderer;
